import React, { useState, useEffect } from 'react';
import { User, Power, RefreshCw, Trash2, Search, Mail, Shield, UserCheck, X, Eye, EyeOff, Info, Briefcase, Building2, Phone, MapPin, Edit3, Hash, ChevronDown, UserPlus, ShieldCheck, CheckCircle2, ArrowRight, Key, AlertCircle } from 'lucide-react';
import adminService from '../../services/adminService';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [editData, setEditData] = useState({});
    const [isSaving, setIsSaving] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);

    // Single expandedRow state: { userId, panelType: 'profile'|'edit'|'password'|'action', actionType?: 'ACTIVATE'|'DEACTIVATE'|'PURGE' }
    const [expandedRow, setExpandedRow] = useState(null);
    
    // Provisioning logic
    const [isProvisioningOpen, setIsProvisioningOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        mail: '',
        username: '',
        domain: 'xevyte.com',
        password: '',
        role: 'USER',
        fullName: '',
        mobile: '',
        designation: '',
        department: '',
        address: '',
        employeeId: ''
    });
    const domains = ['xevyte.com', 'xevyte.co'];
    const [activeTab, setActiveTab] = useState('profile');
    const [isProvisioning, setIsProvisioning] = useState(false);
    const [provStatus, setProvStatus] = useState({ type: null, message: '' });
    const [showProvPassword, setShowProvPassword] = useState(false);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const data = await adminService.getUsers();
            setUsers(data);
        } catch (error) {
            console.error('Failed to fetch users', error);
        }
    };

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setIsProvisioning(true);
        setProvStatus({ type: null, message: '' });

        const finalEmail = `${newUser.username}@${newUser.domain}`;
        try {
            await adminService.createUser({ ...newUser, mail: finalEmail });
            setProvStatus({
                type: 'success',
                message: `User account ${finalEmail} has been successfully created.`
            });
            setNewUser({
                mail: '',
                username: '',
                domain: 'xevyte.com',
                password: '',
                role: 'USER',
                fullName: '',
                mobile: '',
                designation: '',
                department: '',
                address: '',
                employeeId: ''
            });
            setShowProvPassword(false);
            setActiveTab('profile');
            fetchUsers(); // Refresh list
        } catch (error) {
            setProvStatus({
                type: 'error',
                message: error.response?.data?.message || 'Failed to create account.'
            });
        } finally {
            setIsProvisioning(false);
        }
    };

    // Open an inline panel below the user row
    const openPanel = (user, panelType, actionType = null) => {
        if (expandedRow && expandedRow.userId === user.id && expandedRow.panelType === panelType) {
            setExpandedRow(null);
            return;
        }
        setIsProvisioningOpen(false); // Close provisioning when opening individual row details
        setExpandedRow({ userId: user.id, panelType, actionType, mail: user.mail, user });
        if (panelType === 'edit') {
            setEditData({ ...user });
        }
        if (panelType === 'password') {
            setShowResetPassword(false);
        }
    };

    const closePanel = () => setExpandedRow(null);

    const handleUpdateUser = async () => {
        if (!expandedRow) return;
        setIsSaving(true);
        try {
            const profileUpdate = {
                fullName: editData.fullName || '',
                designation: editData.designation || '',
                department: editData.department || '',
                employeeId: editData.employeeId || '',
                mobile: editData.mobile || '',
                address: editData.address || '',
            };
            await adminService.updateUser(expandedRow.userId, profileUpdate);
            fetchUsers();
            alert("User information updated successfully.");
            closePanel();
        } catch (error) {
            console.error('Update failed:', error?.response?.data || error.message);
            alert("Update failed: " + (error?.response?.data?.message || error.message));
        } finally {
            setIsSaving(false);
        }
    };

    const confirmToggleStatus = async () => {
        if (!expandedRow) return;
        try {
            await adminService.toggleUserStatus(expandedRow.userId, expandedRow.actionType !== 'DEACTIVATE');
            fetchUsers();
            closePanel();
        } catch (error) {
            console.error('Status toggle failed');
        }
    };

    const confirmDeleteUser = async () => {
        if (!expandedRow) return;
        try {
            await adminService.deleteUser(expandedRow.userId);
            fetchUsers();
            alert("User deleted successfully.");
            closePanel();
        } catch (error) {
            console.error('Delete failed:', error?.response?.data || error.message);
            alert("Delete failed: " + (error?.response?.data?.message || error.message));
        }
    };

    const handleResetPassword = async (e) => {
        if (e) e.preventDefault();
        if (!expandedRow) return;
        try {
            await adminService.resetPassword(expandedRow.userId, expandedRow.newPassword);
            alert(`Password for ${expandedRow.mail} has been updated.`);
            closePanel();
        } catch (error) {
            console.error('Password reset failed');
        }
    };

    const changeRole = async (userId, role) => {
        try {
            await adminService.assignRole(userId, role);
            fetchUsers();
        } catch (error) {
            console.error('Role update failed');
        }
    };

    const filteredUsers = users
        .filter(u =>
            u.mail.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (u.fullName && u.fullName.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (u.employeeId && u.employeeId.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (u.department && u.department.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (u.designation && u.designation.toLowerCase().includes(searchTerm.toLowerCase()))
        )
        .sort((a, b) => a.mail.localeCompare(b.mail));

    // ─── Inline Panel Renderers ────────────────────────────────────────

    const renderProvisioningPanel = () => {
        return (
            <div className="p-8 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/30 border-b border-[rgb(var(--border))] animate-fade-in shadow-inner">
                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center justify-between mb-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl bg-[#4f46e5]/10 text-[#4f46e5] flex items-center justify-center border border-[#4f46e5]/20 shadow-sm">
                                <UserPlus size={24} />
                            </div>
                            <div>
                                <h3 className="text-lg font-black text-slate-800 dark:text-white uppercase tracking-tight">Provision New Identity</h3>
                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1 italic">Enterprise Directory Layer</p>
                            </div>
                        </div>
                        <div className="flex p-1 bg-slate-100 dark:bg-slate-900/50 rounded-2xl border border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => setActiveTab('profile')}
                                className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'profile' ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-lg shadow-[#4f46e5]/10' : 'text-slate-400'}`}
                            >
                                User Profile
                            </button>
                            <button
                                onClick={() => setActiveTab('security')}
                                className={`px-5 py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${activeTab === 'security' ? 'bg-white dark:bg-slate-800 text-[#4f46e5] shadow-lg shadow-[#4f46e5]/10' : 'text-slate-400'}`}
                            >
                                Security Context
                            </button>
                        </div>
                    </div>

                    {provStatus.type === 'success' ? (
                        <div className="py-10 text-center animate-scale-in">
                            <div className="w-20 h-20 bg-emerald-500/10 text-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-emerald-500/20">
                                <CheckCircle2 size={40} />
                            </div>
                            <h4 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight mb-2">Registry Updated</h4>
                            <p className="text-sm text-slate-500 font-medium mb-6">{provStatus.message}</p>
                            <button
                                onClick={() => setProvStatus({ type: null, message: '' })}
                                className="premium-button-primary px-8 rounded-xl flex items-center mx-auto"
                            >
                                <span>Provision Next</span>
                                <ArrowRight size={18} className="ml-2" />
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={handleCreateUser} className="space-y-8 animate-fade-in">
                            {activeTab === 'profile' ? (
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Full Identity Name</label>
                                            <div className="relative group">
                                                <User className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <input
                                                    required
                                                    className="premium-input w-full !pl-11 !py-3 text-sm font-bold shadow-sm"
                                                    value={newUser.fullName}
                                                    onChange={(e) => setNewUser({ ...newUser, fullName: e.target.value })}
                                                    placeholder="e.g. John Doe"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Recovery Contact</label>
                                            <div className="relative group">
                                                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <input
                                                    className="premium-input w-full !pl-11 !py-3 text-sm font-bold shadow-sm"
                                                    value={newUser.mobile}
                                                    onChange={(e) => setNewUser({ ...newUser, mobile: e.target.value })}
                                                    placeholder="+1 000 000 000"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Designation</label>
                                            <div className="relative group">
                                                <Briefcase className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <input
                                                    className="premium-input w-full !pl-11 !py-3 text-sm font-bold shadow-sm"
                                                    value={newUser.designation}
                                                    onChange={(e) => setNewUser({ ...newUser, designation: e.target.value })}
                                                    placeholder="MTA Engineer"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Infrastructure Unit</label>
                                            <div className="relative group">
                                                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <input
                                                    className="premium-input w-full !pl-11 !py-3 text-sm font-bold shadow-sm"
                                                    value={newUser.department}
                                                    onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                                                    placeholder="IT Operations"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Employee Identifier</label>
                                            <div className="relative group">
                                                <Hash className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <input
                                                    className="premium-input w-full !pl-11 !py-3 text-sm font-bold shadow-sm"
                                                    value={newUser.employeeId}
                                                    onChange={(e) => setNewUser({ ...newUser, employeeId: e.target.value })}
                                                    placeholder="EMP7742"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Physical Address</label>
                                            <div className="relative group">
                                                <MapPin className="absolute left-4 top-4 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                <textarea
                                                    className="premium-input w-full !pl-11 !py-2.5 text-sm font-bold shadow-sm h-[46px] resize-none"
                                                    value={newUser.address}
                                                    onChange={(e) => setNewUser({ ...newUser, address: e.target.value })}
                                                    placeholder="Primary HQ / Remote"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">MTA Root Identifier</label>
                                            <div className="flex items-center gap-3">
                                                <div className="relative flex-1 group">
                                                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" size={16} />
                                                    <input
                                                        required
                                                        className="premium-input w-full !pl-11 !py-3 text-sm font-bold !lowercase"
                                                        value={newUser.username}
                                                        onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                                                        placeholder="username"
                                                    />
                                                </div>
                                                <span className="text-xl font-black text-slate-300">@</span>
                                                <select
                                                    className="premium-input w-[160px] !py-3 text-xs font-black !lowercase appearance-none cursor-pointer"
                                                    value={newUser.domain}
                                                    onChange={(e) => setNewUser({ ...newUser, domain: e.target.value })}
                                                >
                                                    {domains.map(d => <option key={d} value={d}>{d}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">Access Protocol (Role)</label>
                                            <select
                                                className="premium-input w-full !py-3 text-xs font-black uppercase tracking-widest appearance-none cursor-pointer"
                                                value={newUser.role}
                                                onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                                            >
                                                <option value="USER">Standard Registry Access</option>
                                                <option value="MANAGER">High-Level Management</option>
                                                <option value="ADMIN">MTA-ROOT Administrator</option>
                                            </select>
                                        </div>
                                    </div>
                                    <div className="space-y-6">
                                        <div>
                                            <div className="flex justify-between items-center mb-2 px-1">
                                                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block">Access Token (Password)</label>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
                                                        let pass = "";
                                                        pass += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
                                                        pass += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
                                                        pass += "0123456789"[Math.floor(Math.random() * 10)];
                                                        pass += "!@#$%^&*"[Math.floor(Math.random() * 8)];
                                                        for (let i = 0; i < 8; i++) pass += chars[Math.floor(Math.random() * chars.length)];
                                                        pass = pass.split('').sort(() => 0.5 - Math.random()).join('');
                                                        setNewUser({ ...newUser, password: pass });
                                                        setShowProvPassword(true);
                                                    }}
                                                    className="text-[9px] font-black text-[#4f46e5] uppercase flex items-center gap-1 hover:underline"
                                                >
                                                    <RefreshCw size={10} /> Auto-Gen
                                                </button>
                                            </div>
                                            <div className="relative group">
                                                <Key size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-[#4f46e5]" />
                                                <input
                                                    required
                                                    type={showProvPassword ? "text" : "password"}
                                                    className="premium-input w-full !pl-11 !py-3 font-mono text-sm tracking-widest"
                                                    value={newUser.password}
                                                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                                                    placeholder="••••••••••••"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowProvPassword(!showProvPassword)}
                                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#4f46e5]"
                                                >
                                                    {showProvPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                                </button>
                                            </div>
                                        </div>
                                        <div className="p-4 rounded-2xl bg-indigo-500/[0.03] border border-indigo-500/10 grid grid-cols-2 gap-2">
                                            {[
                                                { label: "8+ Length", v: newUser.password.length >= 8 },
                                                { label: "A-Z Scope", v: /[A-Z]/.test(newUser.password) },
                                                { label: "Numeric", v: /[0-9]/.test(newUser.password) },
                                                { label: "Symbol", v: /[!@#$%^&*]/.test(newUser.password) }
                                            ].map((r, i) => (
                                                <div key={i} className={`flex items-center gap-2 ${r.v ? 'text-emerald-500' : 'text-slate-400'}`}>
                                                    <CheckCircle2 size={10} />
                                                    <span className="text-[9px] font-black uppercase tracking-tight">{r.label}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {provStatus.type === 'error' && (
                                <div className="p-4 rounded-2xl bg-red-500/5 border border-red-500/10 flex items-center gap-3 text-red-500 shadow-sm animate-shake">
                                    <AlertCircle size={18} />
                                    <p className="text-[11px] font-black uppercase tracking-widest">{provStatus.message}</p>
                                </div>
                            )}

                            <div className="flex items-center justify-between pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsProvisioningOpen(false)}
                                    className="px-6 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest hover:text-slate-600 dark:hover:text-slate-200 transition-all"
                                >
                                    Cancel Operations
                                </button>
                                <button
                                    type="submit"
                                    disabled={isProvisioning}
                                    className="premium-button-primary px-10 py-3.5 rounded-2xl flex items-center gap-3 shadow-xl shadow-[#4f46e5]/25"
                                >
                                    {isProvisioning ? (
                                        <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />Registry Update...</>
                                    ) : (
                                        <><UserPlus size={18} />Complete Provisioning</>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}
                </div>
            </div>
        );
    };

    const renderActionPanel = () => {
        if (!expandedRow) return null;
        const { actionType, mail } = expandedRow;
        const isPurge = actionType === 'PURGE';
        const isDeactivate = actionType === 'DEACTIVATE';

        return (
            <div className="p-5 flex items-center gap-5 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/40 dark:to-slate-900/60">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0 ${isPurge ? 'bg-red-500/10 text-red-500' : isDeactivate ? 'bg-amber-500/10 text-amber-500' : 'bg-emerald-500/10 text-emerald-500'}`}>
                    {isPurge ? <Trash2 size={20} /> : <Power size={20} />}
                </div>

                <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-slate-800 dark:text-slate-200 uppercase tracking-tight">
                        {isPurge ? 'Permanently purge' : isDeactivate ? 'Suspend access for' : 'Activate access for'}{' '}
                        <span className="font-mono text-[#4f46e5] lowercase tracking-tighter">{mail}</span>?
                    </p>
                    <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-widest">
                        {isPurge ? 'Critical: Wipes all mailbox data and identity history.' : isDeactivate ? 'Identity will be locked from all authenticated routes.' : 'Restores full SMTP and IMAP capabilities.'}
                    </p>
                </div>

                <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                        onClick={closePanel}
                        className="px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-500 border border-[rgb(var(--border))] rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
                    >
                        Discard
                    </button>
                    <button
                        onClick={isPurge ? confirmDeleteUser : confirmToggleStatus}
                        className={`px-6 py-2.5 text-[10px] font-black uppercase tracking-widest text-white rounded-xl hover:scale-[1.03] active:scale-95 transition-all shadow-lg ${isPurge ? 'bg-red-500 shadow-red-500/25' : isDeactivate ? 'bg-amber-500 shadow-amber-500/25' : 'bg-emerald-500 shadow-emerald-500/25'}`}
                    >
                        {isPurge ? 'Purge Identity' : 'Execute'}
                    </button>
                </div>
            </div>
        );
    };

    const renderPasswordPanel = () => {
        if (!expandedRow) return null;
        const newPassword = expandedRow.newPassword || '';

        return (
            <div className="p-6 bg-gradient-to-r from-slate-50 to-white dark:from-slate-800/40 dark:to-slate-900/60">
                <div className="max-w-4xl flex items-start gap-6">
                    <div className="w-12 h-12 rounded-2xl bg-[#4f46e5]/10 text-[#4f46e5] flex items-center justify-center flex-shrink-0 border border-[#4f46e5]/20">
                        <RefreshCw size={24} />
                    </div>

                    <div className="flex-1 min-w-0 space-y-4">
                        <div>
                            <p className="text-base font-black text-slate-800 dark:text-white uppercase tracking-tight">
                                Reset Token for <span className="font-mono text-[#4f46e5] lowercase tracking-tighter">{expandedRow.mail}</span>
                            </p>
                            <p className="text-[10px] text-slate-400 mt-1 font-bold uppercase tracking-widest italic">All current sessions will be terminated upon update.</p>
                        </div>

                        <div className="flex items-center gap-4">
                            <div className="relative flex-1 max-w-sm">
                                <input
                                    type={showResetPassword ? "text" : "password"}
                                    required
                                    className="premium-input w-full pr-12 h-12 text-sm font-mono tracking-widest"
                                    placeholder="Enter new secret..."
                                    value={newPassword}
                                    onChange={(e) => setExpandedRow({ ...expandedRow, newPassword: e.target.value })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowResetPassword(!showResetPassword)}
                                    className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-[#4f46e5]"
                                >
                                    {showResetPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                </button>
                            </div>

                            <button
                                onClick={() => {
                                    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*";
                                    let pass = "";
                                    pass += "ABCDEFGHIJKLMNOPQRSTUVWXYZ"[Math.floor(Math.random() * 26)];
                                    pass += "abcdefghijklmnopqrstuvwxyz"[Math.floor(Math.random() * 26)];
                                    pass += "0123456789"[Math.floor(Math.random() * 10)];
                                    pass += "!@#$%^&*"[Math.floor(Math.random() * 8)];
                                    for (let i = 0; i < 8; i++) pass += chars[Math.floor(Math.random() * chars.length)];
                                    pass = pass.split('').sort(() => 0.5 - Math.random()).join('');
                                    setExpandedRow({ ...expandedRow, newPassword: pass });
                                    setShowResetPassword(true);
                                }}
                                className="px-4 py-3 text-[10px] font-black text-[#4f46e5] uppercase tracking-widest border border-[#4f46e5]/20 rounded-xl hover:bg-[#4f46e5]/5 transition-all flex items-center gap-2 whitespace-nowrap"
                            >
                                <RefreshCw size={14} /> Auto-Generate
                            </button>

                            <button
                                onClick={handleResetPassword}
                                className="px-8 py-3 text-[10px] font-black uppercase tracking-widest text-white bg-[#4f46e5] rounded-xl hover:scale-[1.03] active:scale-95 transition-all shadow-xl shadow-[#4f46e5]/20"
                            >
                                Update Token
                            </button>
                        </div>

                        <div className="flex items-center gap-4">
                            {[
                                { label: "8-16 Chars", v: newPassword.length >= 8 && newPassword.length <= 16 },
                                { label: "Complex Scope", v: /[A-Z]/.test(newPassword) && /[0-9]/.test(newPassword) && /[!@#$%^&*]/.test(newPassword) },
                                { label: "Unique Context", v: newPassword && !['1234', 'password'].some(s => newPassword.includes(s)) }
                            ].map((r, i) => (
                                <div key={i} className={`flex items-center gap-2 ${r.v ? 'text-emerald-500' : 'text-slate-400'}`}>
                                    <div className={`w-1.5 h-1.5 rounded-full ${r.v ? 'bg-emerald-500' : 'bg-slate-300'}`} />
                                    <span className="text-[9px] font-black uppercase tracking-tight">{r.label}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                    <button onClick={closePanel} className="p-2 text-slate-400 hover:bg-slate-200/50 rounded-xl">
                        <X size={20} />
                    </button>
                </div>
            </div>
        );
    };

    const renderProfilePanel = () => {
        if (!expandedRow || !expandedRow.user) return null;
        const user = expandedRow.user;

        return (
            <div className="p-6 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/30">
                <div className="flex items-start gap-8 max-w-6xl">
                    <div className="flex items-center gap-5 flex-shrink-0">
                        <div className="w-20 h-20 rounded-3xl bg-white dark:bg-slate-800 flex items-center justify-center text-3xl font-black text-[#4f46e5] border-2 border-[#4f46e5]/10 shadow-xl">
                            {user.mail.charAt(0).toUpperCase()}
                        </div>
                        <div>
                            <h4 className="text-xl font-black text-slate-900 dark:text-white uppercase tracking-tight leading-none mb-1">{user.fullName || 'Unidentified'}</h4>
                            <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest ${user.enabled ? 'bg-emerald-500/10 text-emerald-500' : 'bg-red-500/10 text-red-500'}`}>{user.enabled ? 'Active Identity' : 'Suspended'}</span>
                                <span className="bg-[#4f46e5]/10 text-[#4f46e5] px-2 py-0.5 rounded-md text-[9px] font-black uppercase tracking-widest">{user.role}</span>
                            </div>
                            <p className="text-[11px] font-mono text-slate-400 lower-case italic tracking-tighter">{user.mail}</p>
                        </div>
                    </div>

                    <div className="flex-1 grid grid-cols-4 gap-4">
                        {[
                            { label: 'Designation', val: user.designation, icon: Briefcase },
                            { label: 'Department', val: user.department, icon: Building2 },
                            { label: 'Employee ID', val: user.employeeId, icon: Hash },
                            { label: 'Direct Dial', val: user.mobile, icon: Phone }
                        ].map((item, i) => (
                            <div key={i} className="p-4 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-[rgb(var(--border))] shadow-sm group hover:border-[#4f46e5]/20 transition-all">
                                <div className="flex items-center gap-2 text-slate-400 mb-2">
                                    <item.icon size={12} className="group-hover:text-[#4f46e5] transition-colors" />
                                    <span className="text-[9px] font-black uppercase tracking-[0.2em]">{item.label}</span>
                                </div>
                                <p className="text-xs font-black text-slate-800 dark:text-slate-100 truncate">{item.val || 'N/A'}</p>
                            </div>
                        ))}
                    </div>

                    <div className="flex-shrink-0 flex items-start gap-4">
                        <div className="p-4 rounded-2xl bg-white/60 dark:bg-slate-900/60 border border-[rgb(var(--border))] shadow-sm min-w-[180px]">
                            <div className="flex items-center gap-2 text-slate-400 mb-2">
                                <MapPin size={12} />
                                <span className="text-[9px] font-black uppercase tracking-[0.2em]">Deployment Location</span>
                            </div>
                            <p className="text-[11px] font-bold text-slate-600 dark:text-slate-400 leading-relaxed uppercase tracking-tight">{user.address || 'Remote Context'}</p>
                        </div>
                        <button onClick={closePanel} className="p-2 text-slate-400 hover:bg-slate-200/50 rounded-xl h-fit transition-all">
                            <X size={20} />
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    const renderEditPanel = () => {
        if (!expandedRow || !expandedRow.user) return null;

        return (
            <div className="p-6 bg-gradient-to-r from-indigo-50/50 via-white to-slate-50 dark:from-slate-800/40 dark:via-slate-900/60 dark:to-slate-800/30">
                <div className="flex items-start gap-8 max-w-6xl mx-auto">
                    <div className="w-12 h-12 rounded-2xl bg-[#4f46e5]/10 text-[#4f46e5] flex items-center justify-center flex-shrink-0 border border-[#4f46e5]/20">
                        <Edit3 size={24} />
                    </div>

                    <div className="flex-1 space-y-6">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-base font-black text-slate-800 dark:text-white uppercase tracking-tight">
                                    Sync Profile Context <span className="text-[#4f46e5] lowercase font-mono tracking-tighter">[{expandedRow.mail}]</span>
                                </p>
                                <p className="text-[10px] text-slate-400 mt-1 uppercase font-bold tracking-widest italic">All changes are broadcasted to the MTA registry instantly.</p>
                            </div>
                            <div className="flex items-center gap-3">
                                <button onClick={closePanel} className="px-6 py-2.5 text-[10px] font-black uppercase text-slate-400 hover:text-slate-600 transition-all">Discard</button>
                                <button
                                    onClick={handleUpdateUser}
                                    disabled={isSaving}
                                    className="premium-button-primary px-8 py-2.5 rounded-xl shadow-xl shadow-[#4f46e5]/20 disabled:opacity-50"
                                >
                                    {isSaving ? 'Syncing...' : 'Commit Changes'}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-5 gap-4">
                            {[
                                { k: 'fullName', l: 'Display Name', p: 'Employee Name' },
                                { k: 'designation', l: 'Designation', p: 'Role' },
                                { k: 'department', l: 'Department', p: 'Unit' },
                                { k: 'employeeId', l: 'Employee ID', p: 'EMP0000' },
                                { k: 'mobile', l: 'Direct Dial', p: '+1...' }
                            ].map(f => (
                                <div key={f.k}>
                                    <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-2 px-1">{f.l}</label>
                                    <input
                                        className="premium-input w-full !py-2.5 text-xs font-bold focus:border-[#4f46e5] focus:ring-[#4f46e5]/10"
                                        value={editData[f.k] || ''}
                                        onChange={(e) => setEditData({ ...editData, [f.k]: e.target.value })}
                                        placeholder={f.p}
                                    />
                                </div>
                            ))}
                        </div>
                        <div>
                            <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1 px-1">Physical/Deployment Address</label>
                            <input
                                className="premium-input w-full !py-2.5 text-xs font-bold focus:border-[#4f46e5] focus:ring-[#4f46e5]/10"
                                value={editData.address || ''}
                                onChange={(e) => setEditData({ ...editData, address: e.target.value })}
                                placeholder="Update global address context..."
                            />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8 animate-fade-in pb-10">
            <header className="flex justify-between items-end">
                <div>
                    <h1 className="text-3xl font-extrabold tracking-tight underline decoration-[#4f46e5]/30 decoration-4 underline-offset-8 uppercase tracking-wider">Mailbox Registry</h1>
                    <p className="text-slate-500 mt-2 font-medium">Core directory of all authenticated organization identities.</p>
                </div>
                <div className="flex items-center space-x-3">
                    <div className="flex items-center text-[#4f46e5] bg-[#4f46e5]/10 px-4 py-2 rounded-2xl text-[10px] font-bold uppercase tracking-[0.1em] border border-[#4f46e5]/20 shadow-sm">
                        <ShieldCheck size={14} className="mr-2 text-emerald-500" />
                        Directory Secure
                    </div>
                </div>
            </header>

            <div className="enterprise-card overflow-hidden !border-slate-200/60 dark:!border-slate-800/60 shadow-xl">

                <div className="p-6 border-b border-[rgb(var(--border))] flex items-center justify-between glass">
                    <div className="flex items-center space-x-4 flex-1 max-w-md">
                        <div className="premium-input flex items-center shadow-sm w-full">
                            <Search size={16} className="text-slate-400 mr-3" />
                            <input
                                type="text"
                                placeholder="Search registry by email, name or ID..."
                                className="bg-transparent border-none p-0 focus:ring-0 text-base w-full placeholder:text-slate-400 font-medium font-mono lowercase tracking-tighter"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="flex items-center space-x-2 text-[10px] font-black text-slate-400 uppercase tracking-widest bg-slate-50 dark:bg-slate-900 px-4 py-2 rounded-xl border border-[rgb(var(--border))] shadow-inner">
                        <UserCheck size={14} className="text-emerald-500" />
                        <span>{users.length} Active Identities</span>
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-slate-50 dark:bg-slate-900/50 border-b border-[rgb(var(--border))]">
                            <tr>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">MTA-Root Identity</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Access Protocol</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Operational Status</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em]">Last Registry Check</th>
                                <th className="px-6 py-4 text-[10px] font-black text-slate-400 uppercase tracking-[0.25em] text-right">Contextual Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-[rgb(var(--border))]">
                            {filteredUsers.map((user) => {
                                const isExpanded = expandedRow && expandedRow.userId === user.id;

                                return (
                                    <React.Fragment key={user.id}>
                                        <tr className={`group transition-all ${isExpanded ? 'bg-[#4f46e5]/[0.03] border-l-4 border-l-[#4f46e5]' : 'hover:bg-slate-50/50 dark:hover:bg-[#4f46e5]/5'}`}>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center space-x-4">
                                                    <div className={`w-11 h-11 rounded-2xl flex items-center justify-center transition-all border border-[rgb(var(--border))] shadow-sm ${isExpanded ? 'bg-[#4f46e5]/10 text-[#4f46e5]' : 'bg-white dark:bg-slate-800 text-slate-500 group-hover:text-[#4f46e5] group-hover:bg-[#4f46e5]/10'}`}>
                                                        <User size={22} />
                                                    </div>
                                                    <div>
                                                        <p className="font-mono font-extrabold text-[#4f46e5] lowercase tracking-tighter text-sm leading-tight italic">{user.mail}</p>
                                                        <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-1">{user.fullName || 'Unassigned Name'}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="inline-flex items-center gap-2 p-1.5 rounded-xl border border-[rgb(var(--border))] bg-white dark:bg-slate-900 shadow-sm pr-3">
                                                    <div className="w-6 h-6 rounded-lg bg-indigo-500/10 text-indigo-500 flex items-center justify-center">
                                                        <Shield size={12} />
                                                    </div>
                                                    <select
                                                        value={user.role}
                                                        onChange={(e) => changeRole(user.id, e.target.value)}
                                                        className="bg-transparent border-none p-0 text-[10px] font-black uppercase tracking-widest focus:ring-0 cursor-pointer text-slate-700 dark:text-slate-300"
                                                    >
                                                        <option value="USER">Standard</option>
                                                        <option value="MANAGER">Elevated</option>
                                                        <option value="ADMIN">Root Admin</option>
                                                    </select>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest ${user.enabled
                                                    ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20'
                                                    : 'bg-slate-400/10 text-slate-400 border border-slate-400/20'
                                                    } shadow-sm`}>
                                                    <span className={`w-1.5 h-1.5 rounded-full mr-2 shadow-inner ${user.enabled ? 'bg-emerald-500 anim-pulse' : 'bg-slate-400'}`}></span>
                                                    {user.enabled ? 'Active Registry' : 'LOCKED IDENTITY'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex flex-col">
                                                    <span className="text-[10px] font-black text-slate-700 dark:text-slate-200 uppercase tracking-widest">Authenticated</span>
                                                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-tight mt-0.5 italic">2 minutes ago</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <button
                                                        onClick={() => openPanel(user, 'profile')}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded && expandedRow.panelType === 'profile' ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'border-indigo-100 dark:border-indigo-900/30 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-[#4f46e5]/10'}`}
                                                        title="Identity Telemetry"
                                                    >
                                                        <Info size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openPanel(user, 'edit')}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded && expandedRow.panelType === 'edit' ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'border-indigo-100 dark:border-indigo-900/30 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-[#4f46e5]/10'}`}
                                                        title="Sync Profile"
                                                    >
                                                        <Edit3 size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openPanel(user, 'password')}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded && expandedRow.panelType === 'password' ? 'bg-[#4f46e5] text-white border-[#4f46e5]' : 'border-indigo-100 dark:border-indigo-900/30 text-[#4f46e5] hover:bg-indigo-50 dark:hover:bg-[#4f46e5]/10'}`}
                                                        title="Token Rotation"
                                                    >
                                                        <RefreshCw size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openPanel(user, 'action', user.enabled ? 'DEACTIVATE' : 'ACTIVATE')}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded && expandedRow.panelType === 'action' && expandedRow.actionType !== 'PURGE' ? 'bg-amber-500 text-white border-amber-500' : 'border-amber-100 dark:border-amber-900/30 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-500/10'}`}
                                                        title={user.enabled ? 'Suspend Access' : 'Restore Access'}
                                                    >
                                                        <Power size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => openPanel(user, 'action', 'PURGE')}
                                                        className={`p-2.5 rounded-xl border transition-all shadow-sm ${isExpanded && expandedRow.actionType === 'PURGE' ? 'bg-red-500 text-white border-red-500' : 'border-red-100 dark:border-red-900/30 text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10'}`}
                                                        title="Permanently Purge"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>

                                        {isExpanded && (
                                            <tr>
                                                <td colSpan="5" className="p-0 border-l-4 border-l-[#4f46e5] border-b-2 border-b-[#4f46e5]/10">
                                                    <div className="animate-fade-in shadow-inner">
                                                        {expandedRow.panelType === 'action' && renderActionPanel()}
                                                        {expandedRow.panelType === 'password' && renderPasswordPanel()}
                                                        {expandedRow.panelType === 'profile' && renderProfilePanel()}
                                                        {expandedRow.panelType === 'edit' && renderEditPanel()}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default UserManagement;
